import toString from 'lodash-es/toString'
import find from 'lodash-es/find'
import reverse from 'lodash-es/reverse'
import Decimal from 'decimal.js'
import { ECPairInterface as ECPair, payments, networks, Network, Transaction as RawTransaction, script, address as addressHelper } from 'bitcoinjs-lib'
import { makeRandom, fromWIF, fromPrivateKey } from 'bitcoinjs-lib/src/ecpair'
import * as classify from 'bitcoinjs-lib/src/classify'
import bs58 from 'bs58'
import createHash from 'create-hash'
import ecc from 'tiny-secp256k1'

import {
  OneChainError,
  bigNumber,
  helper,
  IKeypair,
  IBTCUnspent,
} from '@one-chain/core'

export const {
  generateMnemonic,
  mnemonicToSeed,
  mnemonicToSeedSync,
  deriveFromMnemonic,
  deriveFromMnemonicSync,
} = helper

/**
 * 根据 chainID 获取当前网络
 *
 * @param {string} chainId
 */
export function getNetworkByChainId (chainId: string) {
  let network: Network

  switch (chainId) {
    case 'mainnet':
      network = networks['bitcoin']
      break
    case 'testnet':
      network = networks['testnet']
      break
    case 'regtest':
      network = networks['regtest']
      break
    default:
      throw OneChainError.fromCode(10)
  }

  return network
}

/**
 * sat 转 btc
 *
 * @export
 * @param {bigNumber} sat
 * @returns {Decimal}
 */
export function fromSat (sat: bigNumber): Decimal {
  return (new Decimal(sat)).div(1e8)
}

/**
 * btc 转 sat
 *
 * @export
 * @param {bigNumber} btcOriginal
 * @returns {Decimal}
 */
export function toSat (btcOriginal: bigNumber): Decimal {
  return (new Decimal(btcOriginal)).times(1e8)
}

/**
 * 生成密钥对
 *
 * @param {string} chainId
 */
export function generateKeypair (chainId: string) {
  const network = getNetworkByChainId(chainId)
  const ecpair: ECPair = makeRandom({ network })

  return {
    address: payments.p2pkh({ pubkey: ecpair.publicKey, network }).address,
    publicKey: ecpair.publicKey.toString('hex'),
    privateKey: ecpair.privateKey.toString('hex'),
    wif: ecpair.toWIF(),
  }
}

// todo: 这里的生成过程有问题，不要用
export function deriveFromSeed(seed: Buffer| string, path: string): IKeypair {
  const keypair = helper.deriveFromSeed(seed, path)

  return {
    privateKey: keypair.privateKey.toString('hex'),
    publicKey: keypair.publicKey.toString('hex')
  }
}

/**
 * 私钥推地址
 *
 * 支持多种私钥和 WIF 格式
 *
 * @param {string} privateKey
 * @param {string} chainId
 */
export function privateKeyToAddress (privateKey: string, chainId: string) {
  const network = getNetworkByChainId(chainId)

  let ecpair: ECPair
  if (isValidWIF(privateKey, chainId)) {
    ecpair = fromWIF(privateKey, network)
  }
  else if (isValidPrivateKey(privateKey)) {
    const privateKeyBuf = Buffer.from(privateKey, 'hex')
    ecpair = fromPrivateKey(privateKeyBuf)
  }

  if (!ecpair) {
    throw OneChainError.fromCode(12)
  }

  return {
    address: payments.p2pkh({ pubkey: ecpair.publicKey, network }).address,
    publicKey: ecpair.publicKey.toString('hex'),
    privateKey: ecpair.privateKey.toString('hex'),
    wif: ecpair.toWIF(),
  }
}

/**
 * BTC 常用 checksum 算法
 *
 * 详见 http://learnmeabitcoin.com/glossary/checksum
 *
 * @param {Buffer} message
 */
function calcChecksum (message: Buffer) {
  let hash = createHash('sha256').update(message).digest()
  hash = createHash('sha256').update(hash).digest()
  return hash.slice(0, 4).toString('hex')
}

/**
 * 是否是有效的地址
 *
 * 这种检测算法可以检测 p2sh, p2pkh 等地址，详见 http://learnmeabitcoin.com/glossary/address
 *
 * @param {string} addressStr
 */
export function isValidAddress (addressStr: string) {
  try {
    const addressBuf = bs58.decode(addressStr)
    if (addressBuf.length !== 25) {
      return false
    }
    const checksum = addressBuf.slice(-4).toString('hex')
    const body = addressBuf.slice(0, -4)
    const checksumCompare = calcChecksum(body)
    if (checksum !== checksumCompare) {
      return false
    }
  }
  catch (err) {
    return false
  }
  return true
}

/**
 * 是否是有效的私钥
 *
 * @param {string} privateKeyStr
 */
export function isValidPrivateKey (privateKeyStr: string) {
  try {
    const privateKeyBuf = Buffer.from(privateKeyStr, 'hex')
    // TODO 优化验证私钥的性能
    fromPrivateKey(privateKeyBuf)
  }
  catch (err) {
    return false
  }
  return true
}

/**
 * 是否是有效的 WIF
 *
 * 详见 http://learnmeabitcoin.com/glossary/wif
 *
 * @param {string} wif
 * @param {string} chainId
 */
export function isValidWIF (wif: string, chainId: string) {
  try {
    const network = getNetworkByChainId(chainId)
    // TODO 优化验证 WIF 的性能
    fromWIF(wif, network)
  }
  catch (err) {
    return false
  }
  return true
}

/**
 * 是否是有效的公钥
 *
 * 详见 https://github.com/bitcoinjs/bitcoinjs-lib/blob/c5da44ce9526fa3b5c5ca635c263bdfab2c68862/src/payments/p2pkh.js#L29
 *
 * @param {string} publicKeyStr
 */
export function isValidPublicKey (publicKeyStr: string) {
  const publicKeyBuf = Buffer.from(publicKeyStr, 'hex')
  return ecc.isPoint(publicKeyBuf)
}

/**
 * 解析原始的交易数据
 *
 * TODO 转为通用数据结构，将自定义结构分装到其他库中
 *
 * @export
 * @param {string} hex
 * @returns
 */
export function parseRawTransaction (hex: string, unspents: IBTCUnspent[] = []) {
  const rawTransaction = RawTransaction.fromHex(hex)

  let fee = 0
  if (unspents.length > 0) {
    const inputValue = unspents.map(unspent => unspent.value).reduce((a, c) => a + c)
    const outputValue = rawTransaction.outs.map(out => (out as any).value).reduce((a, c) => a + c)
    fee = inputValue - outputValue
  }

  return {
    txid: rawTransaction.getId(),
    iscoinbase: rawTransaction.isCoinbase(),
    fee: toString(fee),
    block: -1,
    blockhash: '',
    confirmations: -1,
    blocktime: '1970-01-01T00:00:00+0000',
    inputs: rawTransaction.ins.map((input) => {
      const txId = reverse(input.hash).toString('hex')
      const ret = {
        from_address: '',
        from_txid: txId,
        vin_index: input.index,
        value: '0',
        sequence: input.sequence,
      }

      if (unspents.length > 0) {
        const unspent = find(unspents, { txId: txId, vout: input.index })
        ret.from_address = unspent.address
        ret.value = toString(unspent.value)
      }

      return ret
    }),
    outputs: rawTransaction.outs.map((output, index) => {
      const type = classify.output(output.script)
      const asm = script.toASM(output.script)
      switch (type) {
        case 'pubkeyhash':
          return { to_address: addressHelper.fromOutputScript(output.script), vout_index: index, value: toString((output as any).value), type, asm }
        case 'nulldata':
          return { to_address: '', vout_index: 1, value: '0', type, asm }
      }
    }),
  }
}
